import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

const STEPS = [
  { selector: '[data-testid="article-title-input"]', title: "Edit the headline", text: "Click the title and type to change it — no special skills needed." },
  { selector: '[data-testid="article-body-editor"]', title: "Edit this article", text: "Click anywhere in the text and edit it like a document. Misspelled words get a red underline, just like Word." },
  { selector: '[data-testid="toolbar-bold"]', title: "Format your text", text: "Use this toolbar for bold, italics, headings, lists, and links." },
  { selector: '[data-testid="comment-on-image-button"]', title: "Request an image change", text: "Not loving the image? Click here and tell us what you'd rather see." },
  { selector: '[data-testid="comments-panel"]', title: "Leave general feedback", text: "Notes for our team that aren't text edits go here." },
  { selector: '[data-testid="save-changes-button"]', title: "Save your edits", text: "Your changes save straight to the draft — nothing goes live yet." },
  { selector: '[data-testid="open-approve-modal-button"]', title: "Approve when ready", text: "Happy with everything? Sign off here and we'll handle scheduling." },
];

export function GuidedTour({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [steps, setSteps] = useState([]);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (open) {
      setSteps(STEPS.filter((s) => document.querySelector(s.selector)));
      setIdx(0);
      setRect(null);
    }
  }, [open]);

  const measure = useCallback(() => {
    const step = steps[idx];
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (el) setRect(el.getBoundingClientRect());
  }, [steps, idx]);

  useEffect(() => {
    if (!open || !steps.length) return;
    const el = document.querySelector(steps[idx].selector);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(measure, 450);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, idx, steps, measure]);

  if (!open || !steps.length || !rect) return null;

  const step = steps[idx];
  const last = idx === steps.length - 1;
  const finish = () => {
    localStorage.setItem("im_tour_done", "1");
    onClose();
  };

  const below = rect.bottom + 170 < window.innerHeight;
  const top = below ? rect.bottom + 12 : Math.max(12, rect.top - 170);
  const left = Math.min(Math.max(rect.left, 12), Math.max(12, window.innerWidth - 332));

  return createPortal(
    <>
      <div className="fixed inset-0 z-[90]" onClick={finish} data-testid="tour-overlay" />
      <div
        className="fixed z-[95] pointer-events-none rounded-lg"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          boxShadow: "0 0 0 2px #F59E0B, 0 0 0 9999px rgba(10,10,10,0.3)",
        }}
      />
      <div
        className="fixed z-[100] w-[300px] bg-amber-50 border border-amber-300 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.2)] p-4"
        style={{ top, left }}
        data-testid="tour-tooltip"
      >
        <p className="font-heading font-extrabold text-sm text-amber-900 mb-1">{step.title}</p>
        <p className="text-xs text-amber-900/80 leading-relaxed mb-3">{step.text}</p>
        <div className="flex items-center justify-between">
          <span className="font-mono-accent text-[10px] text-amber-700">
            {idx + 1} / {steps.length}
          </span>
          <div className="flex items-center gap-3">
            <button data-testid="tour-skip-button" onClick={finish} className="text-xs text-amber-700 hover:underline">
              Skip
            </button>
            <button
              data-testid="tour-next-button"
              onClick={() => (last ? finish() : setIdx(idx + 1))}
              className="text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3.5 py-1.5 active:scale-95 transition-colors"
            >
              {last ? "Got it!" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
